import { enumerate, capitalize } from 'utils/strings';
import MealTypes from 'models/MealTypes';
import IPlanInfo from 'models/IPlanInfo';
import { DiversifiedPlanGroup } from 'models/DiversifiedPlanGroup';

type PlanName =
  | 'quick_easy'
  | 'high_protein'
  | 'chefs_choice'
  | 'chefs_choice_plus'
  | 'gluten_free'
  | 'four_servings'
  | 'prepared_one_serving'
  | 'jumpstart_program'
  | 'high_fiber';

type LoggedOutPlan = {
  name: PlanName,
  price: number,
  dinner_servings: number,
  selected_extras: number
};

type Extras = {
  dinner: number;
  breakfast: number;
  lunch: number;
};

type Extra = keyof Extras;

type ExtrasInfo = {
  [k in keyof Extras]: {
    servings: number;
    servingPrice: number;
  }
};

enum PlanType {
  MEAL_KIT,
  PREPARED,
  PROGRAMS,
  DIVERSIFIED
}

enum Programs {
  JUMPSTART_PROGRAM = 'jumpstart_program',
}

interface IPlan {
  name: PlanName;
  selectedExtras: Extras;

  readonly type: PlanType;
  readonly servings: number;
  readonly servingPrice: number;
  readonly includedDinners: number;
  readonly planPrice: number;
  readonly extras: number;
  readonly extrasPrice: number;
  readonly extrasInfo: ExtrasInfo;
  readonly selectedExtraList: Extra[];
  readonly subtotalPrice: number;
  readonly totalPrice: number;
  readonly maximumExtras: number;
  readonly availableExtras: Extras;
  readonly mealPreferences: string;
}

// TODO: Re-review for correctness and accordance with business rules
class Plan implements IPlan {
  name: PlanName;
  selectedExtras: Extras;
  isDiversified: boolean;

  static plansInfo: IPlanInfo[];

  constructor(name: PlanName, selectedExtras?: Extras, isDiversified?: boolean) {
    this.name = name;
    this.isDiversified = isDiversified ?? false;
    this.setSelectedExtras(selectedExtras || {
      dinner: 0,
      breakfast: 0,
      lunch: 0,
    });

    this.removeExtraExcess();
  }

  static initialize(plansInfo: IPlanInfo[]): void {
    this.plansInfo ||= plansInfo;
  }

  static get isInitialized(): boolean {
    return !!this.plansInfo;
  }

  static getPlan(
    servings?: number,
    selectedExtras?: Extras,
    name?: PlanName,
    isDiversified?: boolean,
  ): Plan {
    switch (servings) {
      case 4:
        return new Plan('four_servings', selectedExtras);

      case 1:
        return new Plan(name || 'prepared_one_serving', selectedExtras);

      default:
        if (name === 'chefs_choice' && isDiversified === false) {
          return new Plan('chefs_choice_plus', selectedExtras, true);
        }
        return new Plan(name || 'chefs_choice_plus', selectedExtras, isDiversified ?? true);
    }
  }

  static getServingOptions(
    planType: PlanType,
    hasGift = false,
  ): number[] {
    switch (planType) {
      case PlanType.PREPARED:
        return [1];

      case PlanType.PROGRAMS:
        return [1];

      default:
        return hasGift ? [2] : [2, 4];
    }
  }

  static getServingOptionsByPlanName(
    planName: PlanName,
  ): number[] {
    switch (planName) {
      case 'prepared_one_serving':
      case 'jumpstart_program':
        return [1];

      // we want to be able to change between these two plans based on servings
      case 'chefs_choice_plus':
      case 'chefs_choice':
      case 'four_servings':
        return [2, 4];

      default:
        return [2];
    }
  }

  static getExtraDinnerOptions(
    planType: PlanType,
    hasGift = false,
    name?: PlanName,
  ): number[] {
    switch (planType) {
      case PlanType.PREPARED:
        return [0, 2, 4];
      default:
        if (name !== undefined && name.toString() === 'chefs_choice_plus') {
          return [0];
        }
        return hasGift ? [0] : [0, 1];
    }
  }

  setSelectedExtras(extras: Extras): void {
    this.selectedExtras = {
      dinner: Math.min(
        extras.dinner,
        this.availableExtras.dinner,
      ),
      breakfast: Math.min(
        extras.breakfast,
        this.availableExtras.breakfast,
      ),
      lunch: Math.min(
        extras.lunch,
        this.availableExtras.lunch,
      ),
    };
  }

  removeExtraExcess(): void {
    if (this.maximumExtras === 1) {
      const selectedKeys = Object
        .entries(this.selectedExtras)
        .reduce((acc, [key, value]) => {
          // @ts-expect-error TODO: fix null safety
          if (value) acc.push(key);
          return acc;
        }, []);

      this.selectedExtras = { dinner: 0, breakfast: 0, lunch: 0 };

      let index = 0;
      let extraCapacity = this.maximumExtras;

      while (extraCapacity > 0 && index < selectedKeys.length) {
        // @ts-expect-error TODO: fix null safety
        this.selectedExtras[selectedKeys[index]] += 1;
        index = index === selectedKeys.length - 1 ? 0 : index + 1;
        extraCapacity -= 1;
      }
    }
  }

  get type(): PlanType {
    switch (this.name) {
      case 'prepared_one_serving':
        return PlanType.PREPARED;
      case 'jumpstart_program':
        return PlanType.PROGRAMS;
      case 'high_fiber':
        return PlanType.DIVERSIFIED;
      case 'chefs_choice_plus':
        return PlanType.MEAL_KIT;
      default:
        return this.isDiversified ? PlanType.DIVERSIFIED : PlanType.MEAL_KIT;
    }
  }

  title(): string {
    switch (this.name) {
      case 'four_servings':
        return 'Meal Kit: 4 Serving';
      case 'jumpstart_program':
        return 'Ready to Eat: 1 Serving';
      case 'prepared_one_serving':
        return 'Ready To Eat: 1 Serving';
      case 'chefs_choice_plus':
        return 'Meal Kit: 2 Serving';
      case 'chefs_choice':
      case 'gluten_free':
      case 'high_protein':
      case 'high_fiber':
        if (this.isDiversified) {
          return 'Mix & Match: Meal Kit: 2 Serving';
        }
        return 'Meal Kit: 2 Serving';
      default:
        return 'Meal Kit: 2 Serving';
    }
  }

  description(): string {
    if (this.type === PlanType.PREPARED) {
      const dinners = this.includedDinners + this.selectedExtras.dinner;
      return `${this.title()} Plan (${dinners}x Dinners)`;
    }
    if (this.type === PlanType.PROGRAMS) {
      return `${this.title()} Plan (4x Dinners, 4x lunches, 4x breakfasts)`;
    }

    return enumerate([
      `${this.title()} Plan`,
      ...Object
        .entries(this.selectedExtras)
        .reduce((acc, [key, value]) => {
          if (value) {
            acc.push(
              // @ts-expect-error TODO: fix null safety

              `${value} ${capitalize(MealTypes.pluralize(key, value))}`,
            );
          }
          return acc;
        }, []),
    ]);
  }

  get servings(): number {
    switch (this.name) {
      case 'four_servings':
        return 4;

      case 'prepared_one_serving':
        return 1;

      case 'jumpstart_program':
        return 1;

      default:
        return 2;
    }
  }

  get servingPrice(): number {
    if (Plan.plansInfo) {
      return Plan.plansInfo?.find((info) => info?.name === this.name)?.price_per_serving ?? 0;
    }

    throw new Error(
      'You have to call Plan.initialize() with the plans '
        + 'information (IPlansInfo[]) before accessing prices',
    );
  }

  get includedDinners(): number {
    switch (this.name) {
      case 'four_servings':
        return 2;

      case 'prepared_one_serving':
        return 6;

      default:
        return 3;
    }
  }

  // can include all day parts, not just dinners
  get includedMeals(): number {
    switch (this.name) {
      case 'four_servings':
        return 2;

      case 'prepared_one_serving':
        return 6;

      case 'jumpstart_program':
        return 12;

      default:
        if (this.isDiversified) {
          return 3;
        }

        return 3;
    }
  }

  get planCardServings(): string {
    switch (this.name) {
      case 'jumpstart_program':
        return '4x Dinners, 4x lunches, 4x breakfasts';
      case 'high_fiber':
      case 'gluten_free':
      case 'high_protein':
        if (this.isDiversified) {
          return '2 Meal Kits, 3x Ready To Eat 1 Serving, 1x Grocery';
        }
        return `${this.includedDinners.toString()}x Dinners`;
      case 'chefs_choice_plus':
        return '3 Meal Kits, 3x Grocery';
      case 'chefs_choice':
        if (this.isDiversified) {
          return '3 Meal Kits, 2x Ready To Eat 1 Serving';
        }
        return `${this.includedDinners.toString()}x Dinners`;
      default:
        return `${this.includedDinners.toString()}x Dinners`;
    }
  }

  get planPrice(): number {
    if (this.type === PlanType.PROGRAMS) {
      return Plan?.plansInfo
        .find((info) => info?.name === this.name)?.price ?? 0;
    }

    if (this.type === PlanType.DIVERSIFIED) {
      return DiversifiedPlanGroup?.DIVERSIFIED?.pricing?.[this.name]?.[0]?.price ?? 0;
    }

    if (this.name === 'chefs_choice_plus') {
      return DiversifiedPlanGroup?.MEAL_KIT?.pricing?.[this.name]?.[0]?.price ?? 0;
    }

    const includedDinnersPrice = this.servings
        * this.servingPrice
        * this.includedDinners;

    return includedDinnersPrice + this.extrasPrice;
  }

  get extras(): number {
    return Object
      .values(this.selectedExtras)
      .reduce((acc, cur) => acc + cur);
  }

  get extraDinners(): number {
    return this.selectedExtras.dinner;
  }

  get defaultExtraDinners(): number {
    switch (this.servings) {
      case 1:
        return 2;

      default:
        return 1;
    }
  }

  get extrasPrice(): number {
    return (
      this.selectedExtras.dinner
        * this.extrasInfo.dinner.servings
        * this.extrasInfo.dinner.servingPrice
    ) + (
      this.selectedExtras.breakfast
        * this.extrasInfo.breakfast.servings
        * this.extrasInfo.breakfast.servingPrice
    ) + (
      this.selectedExtras.lunch
        * this.extrasInfo.lunch.servings
        * this.extrasInfo.lunch.servingPrice
    );
  }

  get breakfastAndLunchPrice(): number {
    return (
      this.selectedExtras.breakfast
        * this.extrasInfo.breakfast.servings
        * this.extrasInfo.breakfast.servingPrice
    ) + (
      this.selectedExtras.lunch
        * this.extrasInfo.lunch.servings
        * this.extrasInfo.lunch.servingPrice
    );
  }

  get extrasInfo(): ExtrasInfo {
    return {
      dinner: {
        servings: this.servings,
        servingPrice: this.servingPrice,
      },
      breakfast: {
        servings: 4,
        servingPrice: 4.50,
      },
      lunch: {
        servings: 2,
        servingPrice: 9.0,
      },
    };
  }

  get selectedExtraList(): Extra[] {
    return Object
      .entries(this.selectedExtras)
      .reduce<Extra[]>((acc, [key, value]) => (
        acc.concat(Array(value).fill(key))
      ), []);
  }

  // TODO: why is this calculated differently than this.planPrice?
  get subtotalPrice(): number {
    if (this.type === PlanType.PROGRAMS || this.type === PlanType.DIVERSIFIED) {
      return this.planPrice;
    }

    return this.servingPrice * this.servings * (this.includedDinners + this.extraDinners);
  }

  get totalPrice(): number {
    return this.type === PlanType.MEAL_KIT
      ? this.planPrice + this.extrasPrice
      : this.planPrice;
  }

  get maximumExtras(): number {
    switch (this.name) {
      case 'four_servings':
        return 1;

      case 'prepared_one_serving':
        return 4;

      default:
        return 3;
    }
  }

  get availableExtras(): Extras {
    switch (this.name) {
      case 'gluten_free':
      case 'four_servings':
        return { dinner: 1, breakfast: 1, lunch: 1 };

      case 'prepared_one_serving':
        return { dinner: 4, breakfast: 0, lunch: 0 };

      default:
        return { dinner: 3, breakfast: 2, lunch: 2 };
    }
  }

  get mealPreferences(): string {
    switch (this.name) {
      case 'quick_easy':
        return 'Less Prep';

      case 'high_protein':
        return 'High Protein';

      case 'gluten_free':
        return 'Gluten-Free';

      case 'high_fiber':
        return 'High Fiber';

      case 'chefs_choice_plus':
      case 'chefs_choice':
        return "Chef's Choice";

      case 'jumpstart_program':
        return 'Jumpstart';

      default:
        return 'No preference';
    }
  }

  preselectedPlanMessage(): string {
    switch (this.name) {
      case 'four_servings':
        return '4 Serving Plan.';

      case 'quick_easy':
        return '2 Serving Plan with Less Prep preference.';

      case 'high_protein':
        return '2 Serving Plan with High-Protein preference.';

      case 'chefs_choice_plus':
      case 'chefs_choice':
        return '2 Serving Plan with Chefs-Choice preference.';

      case 'gluten_free':
        return '2 Serving Plan with Gluten-Free preference.';

      case 'prepared_one_serving':
        return 'Ready To Eat preference.';
      default:
        return '';
    }
  }

  get displayNameByPlanType(): string {
    switch (this.type) {
      case PlanType.MEAL_KIT:
        return 'Meal Kit';
      case PlanType.PROGRAMS:
        return 'Plant-Based Jumpstart';
      case PlanType.PREPARED:
      default:
        return 'Ready To Eat';
    }
  }

  /**
   For programs, where meal selection isn't allowed, returns a string
   with the meal descriptions for a given plan.
   */
  get planDescription(): string {
    if (this.type !== PlanType.PROGRAMS) {
      return '';
    }
    return Plan.plansInfo?.find((info) => info?.name === this.name)?.description ?? '';
  }

  /**
   * Returns the default extras associated with the given plan
   * @param type : PlanType - type of the plan to get extras for
   * @param name : PlanName - name of the plan to get extras for
   */
  static defaultExtrasByPlan(
    type?: PlanType,
    name?: PlanName,
  ): Extras {
    const preparedExtras = { dinner: 2, breakfast: 0, lunch: 0 };
    const programsExtras = { dinner: 0, breakfast: 0, lunch: 0 };
    const mealkitExtras = { dinner: 1, breakfast: 0, lunch: 0 };

    if (type) {
      switch (type) {
        case PlanType.PREPARED:
          return preparedExtras;
        case PlanType.PROGRAMS:
          return programsExtras;
        default:
        // extras for meal-kit
          return mealkitExtras;
      }
    }
    if (name) {
      switch (name) {
        case 'prepared_one_serving':
          return preparedExtras;
        case 'jumpstart_program':
          return programsExtras;
        default:
          // extras for meal-kit
          return mealkitExtras;
      }
    }
    return { dinner: 1, breakfast: 0, lunch: 0 };
  }
}

export { PlanType, Programs };
export type { PlanName, LoggedOutPlan, Extras, Extra, ExtrasInfo };
export default Plan;
